import axios from 'axios';
import { tryRefresh } from './auth';

// Sempre usa URL relativa — o proxy do Vite (dev) e o servidor (prod) resolvem
const API_BASE_URL = '/api';

const USER_KEY = 'tresco_user';

// ============================================
// Axios instance configurado (para quem usa axios)
// Usa cookies HttpOnly — sem token no JavaScript!
// ============================================
export const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,  // Envia cookies automaticamente
});

// Flag para evitar loop infinito de refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

function processQueue(error: unknown | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
}

// Interceptor para tratar 401 (access_token expirado) — tenta refresh automático
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Se 401 e não é retry e não é rota de auth
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/')
        ) {
            if (isRefreshing) {
                // Se já está fazendo refresh, enfileirar este request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => api(originalRequest));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshed = await tryRefresh();
                if (refreshed) {
                    processQueue(null);
                    return api(originalRequest);
                } else {
                    processQueue(error);
                    localStorage.removeItem(USER_KEY);
                    window.location.reload();
                    return Promise.reject(error);
                }
            } catch (refreshError) {
                processQueue(refreshError);
                localStorage.removeItem(USER_KEY);
                window.location.reload();
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// ============================================
// authFetch - wrapper do fetch() nativo com cookies
// ============================================
/**
 * Wrapper autenticado do fetch() nativo.
 * Usa credentials: 'include' para enviar cookies HttpOnly.
 * Tenta refresh automático em caso de 401.
 */
export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const response = await fetch(input, {
        ...init,
        credentials: 'include',  // Envia cookies automaticamente
    });

    // Se 401 e não é rota de auth, tentar refresh
    if (response.status === 401) {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
        if (!url.includes('/auth/')) {
            const refreshed = await tryRefresh();
            if (refreshed) {
                // Retry com novos cookies
                return fetch(input, {
                    ...init,
                    credentials: 'include',
                });
            }
        }
        localStorage.removeItem(USER_KEY);
        window.location.reload();
    }

    return response;
}

// ============================================
// getAuthHeaders - mantido para retrocompatibilidade
// Retorna objeto vazio já que auth é via cookie agora
// ============================================
export function getAuthHeaders(): Record<string, string> {
    return {};
}

// Exporta a URL base para uso em outros serviços
export { API_BASE_URL };
